import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { cn } from '@/utils/merge';
import { commApi } from './commApi';
import { ROLE_LABELS } from './format';

const FROM = ['shop', 'delivery', 'admin'];
const TO = ['admin', 'shop', 'delivery', 'order'];
const CAPS = [
	{ key: 'can_start', label: 'Ξεκινά' },
	{ key: 'can_reply', label: 'Απαντά' },
];

// Global: ON / OFF. Per city: Όπως global / ON / OFF (NULL = inherit).
const Cell = ({ value, inherited, isCity, onChange }) => {
	if (!isCity) {
		return (
			<button
				type='button'
				onClick={() => onChange(!value)}
				className={cn(
					'w-14 rounded-md px-2 py-1 text-[12px] font-semibold',
					value ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-500',
				)}
			>
				{value ? 'ON' : 'OFF'}
			</button>
		);
	}
	return (
		<select
			className={cn(
				'rounded-md border px-1 py-1 text-[12px]',
				value === null || value === undefined
					? 'border-slate-200 text-slate-400'
					: 'border-[#0f9b9d] text-slate-800',
			)}
			value={value === null || value === undefined ? '' : value ? 'on' : 'off'}
			onChange={e => onChange(e.target.value === '' ? null : e.target.value === 'on')}
		>
			<option value=''>Global ({inherited ? 'ON' : 'OFF'})</option>
			<option value='on'>ON</option>
			<option value='off'>OFF</option>
		</select>
	);
};

// "Από → Προς" matrix, editable by the Super Admin, globally or per city.
export const CommPermissions = ({ cities }) => {
	const [cityId, setCityId] = useState('');
	const [data, setData] = useState(null);

	const load = () => commApi.permissions(cityId || undefined).then(setData);
	useEffect(() => {
		load();
	}, [cityId]); // eslint-disable-line react-hooks/exhaustive-deps

	const row = (list, from, to) => (list || []).find(r => r.from_role === from && r.to_role === to);

	const change = async (from, to, cap, value) => {
		const res = await commApi.setPermission({
			cityId: cityId ? Number(cityId) : null,
			fromRole: from,
			toRole: to,
			values: { [cap]: value },
		});
		if (res?.error)
			toast.error(typeof res.error === 'string' ? res.error : 'Κάποιο πρόβλημα προέκυψε');
		load();
	};

	return (
		<div className='flex flex-col gap-3'>
			<div className='flex flex-wrap items-center gap-2'>
				<span className='text-[14px] text-slate-600'>Ισχύει για:</span>
				<select
					className='h-9 rounded-md border border-slate-300 px-2 text-[14px]'
					value={cityId}
					onChange={e => setCityId(e.target.value)}
				>
					<option value=''>Όλες τις πόλεις (global)</option>
					{(cities || []).map(c => (
						<option key={c.city_id} value={c.city_id}>
							{c.name}
						</option>
					))}
				</select>
				<span className='text-[12px] text-slate-500'>
					Προτεραιότητα: εξαίρεση χρήστη &gt; πόλη &gt; global. «Παραγγελία» = συνομιλίες για
					συγκεκριμένη παραγγελία.
				</span>
			</div>
			{!data && <div className='p-4 text-slate-400'>Φόρτωση…</div>}
			{data && !data.error && (
				<div className='overflow-x-auto rounded-xl border border-slate-200 bg-white p-3'>
					<table className='w-full text-[14px]'>
						<thead>
							<tr className='border-b border-slate-200 text-left text-slate-500'>
								<th className='py-2 pr-3 font-normal'>Από</th>
								<th className='py-2 pr-3 font-normal'>Προς</th>
								{CAPS.map(c => (
									<th key={c.key} className='py-2 pr-3 font-normal'>
										{c.label}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{FROM.map(from =>
								TO.map(to => {
									const g = row(data.global, from, to);
									const c = row(data.city, from, to);
									return (
										<tr key={`${from}-${to}`} className='border-b border-slate-100'>
											<td className='py-1.5 pr-3'>{ROLE_LABELS[from]}</td>
											<td className='py-1.5 pr-3'>{ROLE_LABELS[to]}</td>
											{CAPS.map(cap => (
												<td key={cap.key} className='py-1.5 pr-3'>
													<Cell
														isCity={!!cityId}
														value={cityId ? c?.[cap.key] : g?.[cap.key]}
														inherited={g?.[cap.key]}
														onChange={v => change(from, to, cap.key, v)}
													/>
												</td>
											))}
										</tr>
									);
								}),
							)}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};
